import { useState, useRef, useEffect, useCallback } from "react";
import { IMAGE_PATH } from "../../api/apiConfig";
import {
	COLORS,
	BLIGHT_MONSTER_FILES,
	useGameLoop,
	useCanvas,
} from "./errorPageUtils";

// Desktop dimensions
const WIDTH = 600;
const HEIGHT = 420;
const GROUND_Y = HEIGHT - 48;
const RUNNER_X = 88;
const RUNNER_WIDTH = 52;
const RUNNER_HEIGHT = 115;
const JUMP_VY = -12;
const GRAVITY = 0.5;
const BLIGHT_WIDTH = 60;
const BLIGHT_HEIGHT = 79; // default; actual height varies per spawn
const BLIGHT_HEIGHT_MIN = 38;
const BLIGHT_HEIGHT_MAX = 92;
const BLIGHT_GAP_MIN = 100;
const BLIGHT_GAP_MAX = 280;
// Sporadic spacing: sometimes blights spawn close together, sometimes far apart
const BLIGHT_GAP_TIGHT_MIN = 42;
const BLIGHT_GAP_TIGHT_MAX = 88;
const BLIGHT_GAP_TIGHT_CHANCE = 0.38; // chance to use tight gap (cluster)
const COLLECT_W = 28;
const COLLECT_H = 28;
const COLLECT_GAP_MIN = 60;
const COLLECT_GAP_MAX = 120;

// Mobile dimensions (smaller to fit phone width)
const MOBILE_WIDTH = 375;
const MOBILE_HEIGHT = 667; // Phone screen height
const MOBILE_GROUND_Y = MOBILE_HEIGHT - 40;
const MOBILE_RUNNER_X = 50;
const MOBILE_RUNNER_WIDTH = 60;
const MOBILE_RUNNER_HEIGHT = 79;
const MOBILE_BLIGHT_WIDTH = 42;
const MOBILE_BLIGHT_HEIGHT_MIN = 30;
const MOBILE_BLIGHT_HEIGHT_MAX = 70;
const MOBILE_COLLECT_W = 22;
const MOBILE_COLLECT_H = 22;

// Score system
const STARTING_SCORE = 0;
const SCORE_GAIN_PER_COLLECT = 2;
const STARTING_LIVES = 3;
const SPEED = 5;
const SPEED_RAMP_INTERVAL = 180; // frames between speed increases (~3 s at 60fps)
const SPEED_RAMP_AMOUNT = 0.5;
const MAX_SPEED = 10;
const BLIGHT_GAP_RAMP_FRAMES = 90; // reduce spawn gap every ~1.5 s
const BLIGHT_GAP_MIN_FLOOR = 52;
const BLIGHT_GAP_MAX_FLOOR = 120;

// Mobile-optimized constants (slower pacing, more visible)
const MOBILE_SPEED = 3; // Slower initial speed
const MOBILE_SPEED_RAMP_INTERVAL = 240; // Slower ramp (~4 s at 60fps)
const MOBILE_SPEED_RAMP_AMOUNT = 0.3; // Smaller increments
const MOBILE_MAX_SPEED = 7; // Lower max speed
const MOBILE_BLIGHT_GAP_MIN = 150; // Larger initial gaps
const MOBILE_BLIGHT_GAP_MAX = 350;
const MOBILE_BLIGHT_GAP_RAMP_FRAMES = 120; // Slower gap reduction (~2 s)
const MOBILE_BLIGHT_GAP_MIN_FLOOR = 80;
const MOBILE_BLIGHT_GAP_MAX_FLOOR = 180;

// Walking sprite sheet: side-view-walking-cy.png (6×6 grid = 36 frames)
const WALK_SHEET_COLUMNS = 5;
const WALK_SHEET_ROWS = 2;
const WALK_TOTAL_FRAMES = WALK_SHEET_COLUMNS * WALK_SHEET_ROWS; // 36 — play full sheet
const WALK_FRAME_INTERVAL = 3; // advance every N game frames (full cycle takes longer)

export default function ErrorPageRunner({
	statusCode,
	fullScreen = false,
	onLivesChange,
	onScoreChange,
}) {
	const canvasRef = useRef(null);
	const walkSheetRef = useRef(null);
	const blightImgsRef = useRef({}); // filename -> Image (same pattern as LightRunner enemyImgRefs)
	const walkFrameRef = useRef(0);
	const [score, setScore] = useState(STARTING_SCORE);
	const [gameState, setGameState] = useState("idle");
	const [highScore, setHighScore] = useState(0);
	const [isMobile, setIsMobile] = useState(false);
	const [lives, setLives] = useState(STARTING_LIVES);
	const scoreRef = useRef(STARTING_SCORE);
	const livesRef = useRef(STARTING_LIVES);
	const hitFlashRef = useRef(0); // Frame counter for hit animation
	const runnerYRef = useRef(GROUND_Y - RUNNER_HEIGHT);
	const runnerVyRef = useRef(0);
	const blightsRef = useRef([]);
	const collectiblesRef = useRef([]);
	const nextBlightGapRef = useRef(BLIGHT_GAP_MIN);
	const nextCollectGapRef = useRef(COLLECT_GAP_MIN);
	const lastBlightSpawnRef = useRef(0);
	const lastCollectSpawnRef = useRef(0);
	const frameRef = useRef(0);
	const collectIdRef = useRef(0);
	const jumpCountRef = useRef(0); // Track consecutive jumps (max 2)

	// Canvas setup with DPR - update when mobile state changes
	useEffect(() => {
		const checkMobile = window.innerWidth < 640 || "ontouchstart" in window;
		// Desktop always uses original dimensions, mobile uses mobile dimensions
		const width = checkMobile ? MOBILE_WIDTH : WIDTH;
		const height = checkMobile ? MOBILE_HEIGHT : HEIGHT;

		const canvas = canvasRef.current;
		if (!canvas) {
			return;
		}

		const ctx = canvas.getContext("2d");
		const dpr = window.devicePixelRatio || 1;

		// Set actual size in memory
		canvas.width = width * dpr;
		canvas.height = height * dpr;

		// Scale canvas back to desired size
		canvas.style.width = `${width}px`;
		canvas.style.height = `${height}px`;

		// Scale all drawing operations
		ctx.scale(dpr, dpr);
	}, [isMobile]);

	// Detect mobile device
	useEffect(() => {
		const checkMobile = () => {
			setIsMobile(window.innerWidth < 640 || "ontouchstart" in window);
		};
		checkMobile();
		window.addEventListener("resize", checkMobile);
		return () => window.removeEventListener("resize", checkMobile);
	}, []);

	// On mobile, sync initial runner Y to ground so "tap to play" shows character on the ground (idle only)
	useEffect(() => {
		if (isMobile && gameState === "idle") {
			runnerYRef.current = MOBILE_GROUND_Y - MOBILE_RUNNER_HEIGHT;
		}
	}, [isMobile, gameState]);

	// Load walking sprite sheet (side-view lightkin/creature)
	useEffect(() => {
		const img = new Image();
		img.src = `${IMAGE_PATH}/quest/creatures/spritesheets/LightkinRunSheet.png`;
		img.onload = () => {
			walkSheetRef.current = img;
		};
		return () => {
			img.src = "";
		};
	}, []);

	// Load blight enemy GIFs (same path/pattern as LightRunner: quest/monsters/)
	useEffect(() => {
		const loadEnemy = (filename) => {
			const img = new Image();
			img.src = `${IMAGE_PATH}/quest/monsters/${filename}`;
			img.onload = () => {
				blightImgsRef.current = { ...blightImgsRef.current, [filename]: img };
			};
			return img;
		};
		const imgs = BLIGHT_MONSTER_FILES.map(loadEnemy);
		return () => {
			imgs.forEach((img) => {
				if (img?.src) {
					img.src = "";
				}
			});
			blightImgsRef.current = {};
		};
	}, []);

	const reset = useCallback(() => {
		const checkMobile = window.innerWidth < 640 || "ontouchstart" in window;
		const groundY = checkMobile ? MOBILE_GROUND_Y : GROUND_Y;
		const runnerHeight = checkMobile ? MOBILE_RUNNER_HEIGHT : RUNNER_HEIGHT;
		runnerYRef.current = groundY - runnerHeight;
		runnerVyRef.current = 0;
		walkFrameRef.current = 0;
		lastBlightSpawnRef.current = 0;
		lastCollectSpawnRef.current = 0;
		frameRef.current = 0;
		blightsRef.current = [];
		collectiblesRef.current = [];
		jumpCountRef.current = 0; // Reset jump count
		hitFlashRef.current = 0; // Reset hit flash
		nextBlightGapRef.current = checkMobile
			? MOBILE_BLIGHT_GAP_MIN
			: BLIGHT_GAP_MIN;
		nextCollectGapRef.current = COLLECT_GAP_MIN;
		scoreRef.current = STARTING_SCORE;
		livesRef.current = STARTING_LIVES;
		setScore(STARTING_SCORE);
		setLives(STARTING_LIVES);
		if (onLivesChange) {
			onLivesChange(STARTING_LIVES);
		}
		setGameState("playing");
	}, []);

	// Game loop: update (physics, spawn, collision) + render (draw) — same pattern as LightRunner useGameLoop
	const update = useCallback(() => {
		frameRef.current += 1;
		const t = frameRef.current;

		// Check if mobile (re-check on each frame in case of resize)
		const checkMobile = window.innerWidth < 640 || "ontouchstart" in window;

		// Desktop always uses original constants, mobile uses mobile dimensions
		const gameWidth = checkMobile ? MOBILE_WIDTH : WIDTH;
		const gameHeight = checkMobile ? MOBILE_HEIGHT : HEIGHT;
		const gameGroundY = checkMobile ? MOBILE_GROUND_Y : GROUND_Y;
		const gameRunnerX = checkMobile ? MOBILE_RUNNER_X : RUNNER_X;
		const gameRunnerWidth = checkMobile ? MOBILE_RUNNER_WIDTH : RUNNER_WIDTH;
		const gameRunnerHeight = checkMobile ? MOBILE_RUNNER_HEIGHT : RUNNER_HEIGHT;
		const gameBlightWidth = checkMobile ? MOBILE_BLIGHT_WIDTH : BLIGHT_WIDTH;
		const gameBlightHeightMin = checkMobile
			? MOBILE_BLIGHT_HEIGHT_MIN
			: BLIGHT_HEIGHT_MIN;
		const gameBlightHeightMax = checkMobile
			? MOBILE_BLIGHT_HEIGHT_MAX
			: BLIGHT_HEIGHT_MAX;
		const gameCollectW = checkMobile ? MOBILE_COLLECT_W : COLLECT_W;
		const gameCollectH = checkMobile ? MOBILE_COLLECT_H : COLLECT_H;

		// Decrease hit flash counter
		if (hitFlashRef.current > 0) {
			hitFlashRef.current -= 1;
		}

		// Use mobile-optimized constants if on mobile
		const baseSpeed = checkMobile ? MOBILE_SPEED : SPEED;
		const speedRampInterval = checkMobile
			? MOBILE_SPEED_RAMP_INTERVAL
			: SPEED_RAMP_INTERVAL;
		const speedRampAmount = checkMobile
			? MOBILE_SPEED_RAMP_AMOUNT
			: SPEED_RAMP_AMOUNT;
		const maxSpeed = checkMobile ? MOBILE_MAX_SPEED : MAX_SPEED;
		const blightGapMin = checkMobile ? MOBILE_BLIGHT_GAP_MIN : BLIGHT_GAP_MIN;
		const blightGapMax = checkMobile ? MOBILE_BLIGHT_GAP_MAX : BLIGHT_GAP_MAX;
		const blightGapRampFrames = checkMobile
			? MOBILE_BLIGHT_GAP_RAMP_FRAMES
			: BLIGHT_GAP_RAMP_FRAMES;
		const blightGapMinFloor = checkMobile
			? MOBILE_BLIGHT_GAP_MIN_FLOOR
			: BLIGHT_GAP_MIN_FLOOR;
		const blightGapMaxFloor = checkMobile
			? MOBILE_BLIGHT_GAP_MAX_FLOOR
			: BLIGHT_GAP_MAX_FLOOR;

		// Ramp difficulty over time: faster scroll + more frequent blights
		const speedRamp = Math.min(
			maxSpeed - baseSpeed,
			Math.floor(t / speedRampInterval) * speedRampAmount
		);
		const currentSpeed = baseSpeed + speedRamp;
		const blightGapReduction = Math.floor(t / blightGapRampFrames);
		const gapMin = Math.max(
			blightGapMinFloor,
			blightGapMin - blightGapReduction * (checkMobile ? 4 : 6)
		);
		const gapMax = Math.max(
			blightGapMaxFloor,
			blightGapMax - blightGapReduction * (checkMobile ? 6 : 8)
		);
		const nextGapRange = Math.max(24, gapMax - gapMin);

		runnerVyRef.current += GRAVITY;
		runnerYRef.current += runnerVyRef.current;
		const onGround = runnerYRef.current >= gameGroundY - gameRunnerHeight - 1;
		if (onGround) {
			runnerYRef.current = gameGroundY - gameRunnerHeight;
			runnerVyRef.current = 0;
			jumpCountRef.current = 0; // Reset jump count when on ground
		}
		if (onGround && t % WALK_FRAME_INTERVAL === 0) {
			walkFrameRef.current = (walkFrameRef.current + 1) % WALK_TOTAL_FRAMES;
		}

		if (t - lastBlightSpawnRef.current > nextBlightGapRef.current) {
			const gif =
				BLIGHT_MONSTER_FILES[
					Math.floor(Math.random() * BLIGHT_MONSTER_FILES.length)
				];
			const blightHeight =
				gameBlightHeightMin +
				Math.floor(
					Math.random() * (gameBlightHeightMax - gameBlightHeightMin + 1)
				);
			blightsRef.current.push({
				id: `b-${t}`,
				x: gameWidth + gameBlightWidth,
				y: gameGroundY - blightHeight,
				width: gameBlightWidth,
				height: blightHeight,
				gif,
			});
			lastBlightSpawnRef.current = t;
			// Sporadic: sometimes tight cluster (small gap), sometimes normal/wide gap
			// On mobile, reduce tight gap chance for better visibility
			const tightChance = checkMobile
				? BLIGHT_GAP_TIGHT_CHANCE * 0.5
				: BLIGHT_GAP_TIGHT_CHANCE;
			const useTight = Math.random() < tightChance;
			nextBlightGapRef.current = useTight
				? BLIGHT_GAP_TIGHT_MIN +
					Math.floor(
						Math.random() * (BLIGHT_GAP_TIGHT_MAX - BLIGHT_GAP_TIGHT_MIN + 1)
					)
				: gapMin + Math.floor(Math.random() * nextGapRange);
		}

		if (t - lastCollectSpawnRef.current > nextCollectGapRef.current) {
			const cx = gameWidth + gameCollectW;
			const cy = gameGroundY - gameCollectH - 8;
			const overlapsBlight = blightsRef.current.some(
				(b) =>
					cx + gameCollectW > b.x &&
					cx < b.x + b.width &&
					cy + gameCollectH > b.y &&
					cy < b.y + b.height
			);
			if (!overlapsBlight) {
				collectiblesRef.current.push({
					id: `c-${++collectIdRef.current}`,
					type: "light",
					x: cx,
					y: cy,
					width: gameCollectW,
					height: gameCollectH,
				});
				nextCollectGapRef.current =
					COLLECT_GAP_MIN +
					Math.floor(Math.random() * (COLLECT_GAP_MAX - COLLECT_GAP_MIN));
			}
			lastCollectSpawnRef.current = t;
		}

		blightsRef.current = blightsRef.current
			.map((b) => ({ ...b, x: b.x - currentSpeed }))
			.filter((b) => b.x + b.width > 0);
		collectiblesRef.current = collectiblesRef.current
			.map((c) => ({ ...c, x: c.x - currentSpeed }))
			.filter((c) => c.x + c.width > 0);

		// Use appropriate runner position based on device
		const rx = gameRunnerX;
		const ry = runnerYRef.current;
		const rw = gameRunnerWidth;
		const rh = gameRunnerHeight;

		collectiblesRef.current = collectiblesRef.current.filter((c) => {
			const overlap =
				rx + rw > c.x &&
				rx < c.x + c.width &&
				ry + rh > c.y &&
				ry < c.y + c.height;
			if (overlap) {
				// Increment score when collecting lumens
				scoreRef.current += SCORE_GAIN_PER_COLLECT;
				setScore(scoreRef.current);
				// Update high score when game ends, not during play
				return false;
			}
			return true;
		});

		const hit = blightsRef.current.some(
			(b) =>
				rx + rw > b.x &&
				rx < b.x + b.width &&
				ry + rh > b.y &&
				ry < b.y + b.height
		);
		if (hit && hitFlashRef.current === 0) {
			// Lose a life on hit
			livesRef.current -= 1;
			setLives(livesRef.current);
			if (onLivesChange) {
				onLivesChange(livesRef.current);
			}
			hitFlashRef.current = 30; // Flash for 30 frames (~0.5s at 60fps)

			// Remove the blight that was hit
			const hitBlightIndex = blightsRef.current.findIndex(
				(b) =>
					rx + rw > b.x &&
					rx < b.x + b.width &&
					ry + rh > b.y &&
					ry < b.y + b.height
			);
			if (hitBlightIndex !== -1) {
				blightsRef.current.splice(hitBlightIndex, 1);
			}

			// Game over when all lives are lost - set final score as high score
			if (livesRef.current <= 0) {
				setHighScore((h) => Math.max(h, scoreRef.current));
				setGameState("over");
			}
		}
	}, []);

	const render = useCallback(() => {
		const canvas = canvasRef.current;
		if (!canvas) {
			return;
		}
		const ctx = canvas.getContext("2d");
		if (!ctx) {
			return;
		}

		// Get dimensions based on device - desktop always uses original constants
		const checkMobile = window.innerWidth < 640 || "ontouchstart" in window;
		const gameWidth = checkMobile ? MOBILE_WIDTH : WIDTH;
		const gameHeight = checkMobile ? MOBILE_HEIGHT : HEIGHT;
		const gameGroundY = checkMobile ? MOBILE_GROUND_Y : GROUND_Y;
		const runnerX = checkMobile ? MOBILE_RUNNER_X : RUNNER_X;
		const runnerWidth = checkMobile ? MOBILE_RUNNER_WIDTH : RUNNER_WIDTH;
		const runnerHeight = checkMobile ? MOBILE_RUNNER_HEIGHT : RUNNER_HEIGHT;
		const collectRadius = checkMobile ? 11 : 14;

		const ry = runnerYRef.current;

		// Hit flash effect
		if (hitFlashRef.current > 0) {
			ctx.fillStyle = `rgba(239, 68, 68, ${0.3 * (hitFlashRef.current / 30)})`; // Red flash
			ctx.fillRect(0, 0, gameWidth, gameHeight);
		}

		// Background — on-brand light gradient (amber/indigo like app)
		ctx.fillStyle = "#fef9c3"; // amber-100
		ctx.fillRect(0, 0, gameWidth, gameHeight);
		ctx.fillStyle = "#fef3c7"; // amber-100 slightly darker
		ctx.fillRect(0, 0, gameWidth, gameHeight * 0.7);
		ctx.fillStyle = "#fde68a"; // amber-200
		ctx.fillRect(0, gameGroundY - 16, gameWidth, gameHeight);
		ctx.fillStyle = "#fcd34d"; // amber-300 ground
		ctx.fillRect(0, gameGroundY, gameWidth, gameHeight - gameGroundY);

		collectiblesRef.current.forEach((c) => {
			const cx = c.x + c.width / 2;
			const cy = c.y + c.height / 2;
			const r = collectRadius;
			// Orb style to match FlyingOrb: radial gradient (bright center → amber edge) + highlight
			const orbGrad = ctx.createRadialGradient(
				cx - r * 0.3,
				cy - r * 0.3,
				0,
				cx,
				cy,
				r
			);
			orbGrad.addColorStop(0, "#fef9c3"); // yellow-100 (bright center)
			orbGrad.addColorStop(0.4, "#fdba74"); // orange-300
			orbGrad.addColorStop(0.75, "#f59e0b"); // amber-500
			orbGrad.addColorStop(1, "#d97706"); // amber-600 edge
			ctx.shadowColor = "rgba(251, 191, 36, 0.6)";
			ctx.shadowBlur = 12;
			ctx.fillStyle = orbGrad;
			ctx.beginPath();
			ctx.arc(cx, cy, r, 0, Math.PI * 2);
			ctx.fill();
			ctx.shadowBlur = 0;
			// Top-left white shine (like FlyingOrb)
			const shineGrad = ctx.createRadialGradient(
				cx - r * 0.5,
				cy - r * 0.5,
				0,
				cx - r * 0.5,
				cy - r * 0.5,
				r * 0.6
			);
			shineGrad.addColorStop(0, "rgba(255, 255, 255, 0.9)");
			shineGrad.addColorStop(0.5, "rgba(255, 255, 255, 0.3)");
			shineGrad.addColorStop(1, "transparent");
			ctx.fillStyle = shineGrad;
			ctx.beginPath();
			ctx.arc(cx, cy, r, 0, Math.PI * 2);
			ctx.fill();
		});

		blightsRef.current.forEach((b) => {
			const img = b.gif ? blightImgsRef.current[b.gif] : null;
			if (img?.complete && img.naturalWidth) {
				ctx.drawImage(img, b.x, b.y, b.width, b.height);
			} else {
				ctx.fillStyle = COLORS.blight.primary;
				ctx.beginPath();
				ctx.ellipse(
					b.x + b.width / 2,
					b.y + b.height / 2,
					b.width / 2 - 2,
					b.height / 2 - 2,
					0,
					0,
					Math.PI * 2
				);
				ctx.fill();
			}
		});

		const img = walkSheetRef.current;
		if (img?.complete && img.naturalWidth) {
			const cols = WALK_SHEET_COLUMNS;
			const rows = WALK_SHEET_ROWS;
			const frameW = img.width / cols;
			const frameH = img.height / rows;
			const frameIndex = walkFrameRef.current % WALK_TOTAL_FRAMES;
			const sx = (frameIndex % cols) * frameW;
			const sy = Math.floor(frameIndex / cols) * frameH;
			ctx.drawImage(
				img,
				sx,
				sy,
				frameW,
				frameH,
				runnerX,
				ry,
				runnerWidth,
				runnerHeight
			);
		} else {
			ctx.fillStyle = COLORS.light.primary;
			ctx.shadowColor = COLORS.light.glow;
			ctx.shadowBlur = 10;
			ctx.fillRect(runnerX, ry, runnerWidth, runnerHeight);
			ctx.shadowBlur = 0;
			ctx.fillStyle = "white";
			// Eye positions proportional to runner size
			const eyeOffset = runnerWidth * 0.27;
			const eyeSize = Math.max(5, runnerWidth * 0.13);
			ctx.fillRect(
				runnerX + eyeOffset,
				ry + runnerHeight * 0.27,
				eyeSize,
				eyeSize
			);
			ctx.fillRect(
				runnerX + runnerWidth * 0.58,
				ry + runnerHeight * 0.27,
				eyeSize,
				eyeSize
			);
		}

		// HUD: draw lumen orb and score (only when not fullScreen — overlay shows them on 500 page)
		if (!fullScreen) {
			const drawHudOrb = (ox, oy, r) => {
				const og = ctx.createRadialGradient(
					ox - r * 0.3,
					oy - r * 0.3,
					0,
					ox,
					oy,
					r
				);
				og.addColorStop(0, "#fef9c3");
				og.addColorStop(0.4, "#fdba74");
				og.addColorStop(0.75, "#f59e0b");
				og.addColorStop(1, "#d97706");
				ctx.shadowColor = "rgba(251, 191, 36, 0.5)";
				ctx.shadowBlur = 6;
				ctx.fillStyle = og;
				ctx.beginPath();
				ctx.arc(ox, oy, r, 0, Math.PI * 2);
				ctx.fill();
				ctx.shadowBlur = 0;
				const sg = ctx.createRadialGradient(
					ox - r * 0.5,
					oy - r * 0.5,
					0,
					ox - r * 0.5,
					oy - r * 0.5,
					r * 0.6
				);
				sg.addColorStop(0, "rgba(255,255,255,0.85)");
				sg.addColorStop(0.6, "transparent");
				ctx.fillStyle = sg;
				ctx.beginPath();
				ctx.arc(ox, oy, r, 0, Math.PI * 2);
				ctx.fill();
			};

			const hudR = checkMobile ? 6 : 8;
			const hudX = gameWidth - 14;
			const scoreY = checkMobile ? 16 : 20;
			const fontSize = checkMobile ? 14 : 17;

			drawHudOrb(hudX - (checkMobile ? 32 : 42), scoreY, hudR);
			ctx.fillStyle = "#78350f";
			ctx.font = `${fontSize}px sans-serif`;
			ctx.textAlign = "right";
			ctx.fillText(`${scoreRef.current}`, hudX, scoreY + 4);
		}
	}, [fullScreen, onLivesChange]);

	// Notify parent of lives and score changes
	useEffect(() => {
		if (onLivesChange) {
			onLivesChange(lives);
		}
	}, [lives, onLivesChange]);

	useEffect(() => {
		if (onScoreChange) {
			onScoreChange(score);
		}
	}, [score, onScoreChange]);

	// Render game even when idle, but only update when playing
	useGameLoop(
		() => {
			if (gameState === "playing") {
				update();
			}
		},
		render,
		true // Always render so game is visible even when idle
	);

	// Initial render to show game immediately
	useEffect(() => {
		render();
	}, [render]);

	useEffect(() => {
		const handleKeyDown = (e) => {
			if (e.code === "Space") {
				e.preventDefault();
				if (gameState === "idle" || gameState === "over") {
					reset();
				} else if (gameState === "playing") {
					// Only allow jump if jump count is less than 2 (double jump max)
					if (jumpCountRef.current < 2) {
						runnerVyRef.current = JUMP_VY;
						jumpCountRef.current += 1;
					}
				}
			}
		};
		window.addEventListener("keydown", handleKeyDown);
		return () => window.removeEventListener("keydown", handleKeyDown);
	}, [gameState, reset]);

	const handleJump = () => {
		if (gameState === "idle" || gameState === "over") {
			reset();
		} else if (gameState === "playing") {
			// Only allow jump if jump count is less than 2 (double jump max)
			if (jumpCountRef.current < 2) {
				runnerVyRef.current = JUMP_VY;
				jumpCountRef.current += 1;
			}
		}
	};

	// Responsive scale: fit within available container space
	const containerRef = useRef(null);
	const [displayScale, setDisplayScale] = useState(fullScreen ? 1 : 0.6);
	const [currentDims, setCurrentDims] = useState({
		width: WIDTH,
		height: HEIGHT,
	});

	useEffect(() => {
		const checkMobile = window.innerWidth < 640 || "ontouchstart" in window;
		// Only use mobile dimensions on mobile, desktop always uses original
		const dims = checkMobile
			? {
					width: MOBILE_WIDTH,
					height: MOBILE_HEIGHT,
				}
			: {
					width: WIDTH,
					height: HEIGHT,
				};
		setCurrentDims(dims);
	}, [isMobile]);

	useEffect(() => {
		const el = containerRef.current;
		if (!el) {
			return;
		}
		const updateScale = () => {
			if (!el) {
				return;
			}
			const w = el.clientWidth;
			const h = el.clientHeight;
			const checkMobile = window.innerWidth < 640 || "ontouchstart" in window;
			const dims = checkMobile
				? {
						width: MOBILE_WIDTH,
						height: MOBILE_HEIGHT,
					}
				: {
						width: WIDTH,
						height: HEIGHT,
					};
			setCurrentDims(dims);

			if (fullScreen) {
				// For full screen, scale to fit with equal margins
				// On mobile, use full screen height (phone screen height)
				if (checkMobile) {
					// Mobile: use full viewport height, scale width to maintain aspect ratio
					// This makes it the same height as phone screen
					const scale = h / MOBILE_HEIGHT;
					setDisplayScale(Math.max(0.4, scale));
				} else {
					// Desktop: use original dimensions (WIDTH, HEIGHT) with equal margins
					const padding = Math.min(w * 0.05, h * 0.05);
					const availableW = w - padding * 2;
					const availableH = h - padding * 2;
					// Use original desktop dimensions for scaling
					const scale = Math.min(availableW / WIDTH, availableH / HEIGHT);
					setDisplayScale(Math.max(0.5, scale));
				}
			} else {
				// Desktop: scale to fit available space, with max scale of 0.85 for medium screens
				// Use original desktop dimensions
				const scale = Math.min(w / WIDTH, h / HEIGHT, 0.85);
				setDisplayScale(Math.max(0.4, scale)); // Minimum scale of 0.4
			}
		};
		updateScale();
		const ro = new ResizeObserver(updateScale);
		ro.observe(el);
		window.addEventListener("resize", updateScale);
		return () => {
			ro.disconnect();
			window.removeEventListener("resize", updateScale);
		};
	}, [fullScreen, isMobile]);

	if (fullScreen) {
		const isMobileScreen = window.innerWidth < 640;
		// Desktop uses original dimensions, mobile uses mobile dimensions
		const gameWidth = isMobileScreen ? currentDims.width : WIDTH;
		const gameHeight = isMobileScreen ? currentDims.height : HEIGHT;

		return (
			<div className="absolute inset-0 flex items-center justify-center overflow-hidden">
				<div
					ref={containerRef}
					className="flex w-full items-center justify-center"
					style={{
						height: isMobileScreen ? "100vh" : "100%",
						maxHeight: "100%",
						maxWidth: "100%",
						overflow: "hidden",
					}}
				>
					<div
						className="touch-manipulation select-none"
						style={{
							transform: `scale(${displayScale})`,
							transformOrigin: "center",
							width: gameWidth,
							height: gameHeight,
						}}
					>
						<div
							className="relative overflow-hidden rounded-xl"
							style={{ width: gameWidth, height: gameHeight }}
						>
							<canvas
								ref={canvasRef}
								width={gameWidth}
								height={gameHeight}
								className="block cursor-pointer touch-manipulation"
								style={{ width: gameWidth, height: gameHeight }}
								onClick={handleJump}
								onTouchEnd={(e) => {
									e.preventDefault();
									handleJump();
								}}
								role="button"
								tabIndex={0}
								onKeyDown={(e) => e.key === " " && handleJump()}
								aria-label="Jump to play; lightkin collects light orbs, avoid blights"
								title="Space or tap to jump"
							/>
							{gameState === "idle" && (
								<div
									className="pointer-events-none absolute inset-0 flex cursor-pointer touch-manipulation flex-col items-center justify-center"
									style={{ touchAction: "manipulation" }}
								>
									<div
										className="pointer-events-auto rounded-lg border border-amber-200/50 bg-white/70 px-4 py-2 backdrop-blur-sm"
										onClick={reset}
										role="button"
										tabIndex={0}
										onKeyDown={(e) => e.key === "Enter" && reset()}
										aria-label="Start game"
									>
										<span className="text-xs font-medium">
											{isMobile
												? "Tap to play"
												: "Click or press Space to play"}
										</span>
									</div>
								</div>
							)}
							{gameState === "over" && (
								<div
									className="absolute inset-0 flex cursor-pointer touch-manipulation flex-col items-center justify-center bg-amber-100/20 text-amber-900 dark:bg-amber-900/10 dark:text-amber-100"
									style={{ touchAction: "manipulation" }}
									onClick={reset}
									role="button"
									tabIndex={0}
									onKeyDown={(e) => e.key === "Enter" && reset()}
									aria-label="Play again"
								>
									<div className="rounded-lg border border-amber-200/50 bg-white/70 px-4 py-2.5 backdrop-blur-sm">
										<span className="text-xs font-bold">Score: {score}</span>
										<span className="mt-1 block text-xs opacity-90">
											Tap to play
										</span>
									</div>
								</div>
							)}
						</div>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="flex h-full w-full max-w-[700px] flex-col items-center justify-center overflow-hidden">
			<p className="mb-1.5 shrink-0 text-center text-xs font-medium text-gray-600 dark:text-gray-400 sm:mb-2 sm:text-sm">
				While you wait lets do some training. Dodge the Blight and Collect light
				orbs.
			</p>
			<div
				ref={containerRef}
				className="flex w-full flex-1 flex-col items-center justify-center overflow-hidden"
				style={{
					maxHeight: "100%",
					minHeight: 0,
				}}
			>
				<div
					className="touch-manipulation select-none"
					style={{
						transform: `scale(${displayScale})`,
						transformOrigin: "center",
					}}
				>
					<div
						className="relative overflow-hidden rounded-xl border border-amber-200/80 bg-amber-50/50 shadow-inner dark:border-amber-800/50 dark:bg-amber-900/10"
						style={{ width: WIDTH, height: HEIGHT, borderRadius: "0.75rem" }}
					>
						<canvas
							ref={canvasRef}
							width={WIDTH}
							height={HEIGHT}
							className="block cursor-pointer touch-manipulation"
							style={{ width: WIDTH, height: HEIGHT }}
							onClick={handleJump}
							onTouchEnd={(e) => {
								e.preventDefault();
								handleJump();
							}}
							role="button"
							tabIndex={0}
							onKeyDown={(e) => e.key === " " && handleJump()}
							aria-label="Jump to play; lightkin collects light orbs, avoid blights"
							title="Space or tap to jump"
						/>
						{gameState === "idle" && (
							<div
								className="pointer-events-none absolute inset-0 flex cursor-pointer touch-manipulation flex-col items-center justify-center"
								style={{ touchAction: "manipulation" }}
							>
								<div
									className="pointer-events-auto rounded-lg border border-amber-200/50 bg-white/70 px-4 py-2 backdrop-blur-sm"
									onClick={reset}
									role="button"
									tabIndex={0}
									onKeyDown={(e) => e.key === "Enter" && reset()}
									aria-label="Start game"
								>
									<span className="text-xs font-medium">
										{isMobile ? "Tap to play" : "Click or press Space to play"}
									</span>
								</div>
							</div>
						)}
						{gameState === "over" && (
							<div
								className="absolute inset-0 flex cursor-pointer touch-manipulation flex-col items-center justify-center bg-amber-100/20 text-amber-900 dark:bg-amber-900/10 dark:text-amber-100"
								style={{ touchAction: "manipulation" }}
								onClick={reset}
								role="button"
								tabIndex={0}
								onKeyDown={(e) => e.key === "Enter" && reset()}
								aria-label="Play again"
							>
								<div className="rounded-lg border border-amber-200/50 bg-white/70 px-4 py-2.5 backdrop-blur-sm">
									<span className="text-xs font-bold">Score: {score}</span>
									<span className="mt-1 block text-xs opacity-90">
										Tap to play
									</span>
								</div>
							</div>
						)}
					</div>
				</div>
			</div>
			{highScore > 0 && (
				<p className="mt-1.5 flex items-center justify-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 sm:mt-2">
					<span
						className="inline-block h-3 w-3 shrink-0 rounded-full bg-gradient-to-br from-yellow-100 via-orange-300 to-yellow-200"
						aria-hidden
					/>
					Best {highScore}
				</p>
			)}
		</div>
	);
}
